import { User } from 'lucide-react';
import ScrollToTop from './ScrollToTop';
// src/QuoteDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import GlobalSearch from './GlobalSearch';
import HelpCenter from './HelpCenter';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import { useQuote } from './QuoteContext';
import { motion, AnimatePresence } from 'framer-motion';
import InvoiceModal from './InvoiceModal';
import { buildInvoicePDF } from './InvoicePDF';
import QuoteNotes from './QuoteNotes';
import ClientsPanel from './ClientsPanel';
import { sendQuoteEmail } from './emailService';

const STATUTS = {
  en_cours: { label: 'En cours',  color: '#f59e0b', bg: '#fef3c7' },
  gagne:    { label: 'Gagné',     color: '#10b981', bg: '#d1fae5' },
  perdu:    { label: 'Perdu',     color: '#ef4444', bg: '#fee2e2' },
  archive:  { label: 'Archivé',   color: '#6b7280', bg: '#f3f4f6' },
};

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
const fmtDate = (d) => {
  if (!d) return '—';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};
const todayStr = () => new Date().toISOString().split('T')[0];

const Btn = ({ onClick, bg, color, border, hoverBg, hoverBorder, hoverColor, children, style = {} }) => {
  const [hovered, setHovered] = React.useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? (hoverBg || bg) : bg, color: hovered ? (hoverColor || color) : color,
        border: `1px solid ${hovered ? (hoverBorder || border) : border}`,
        borderRadius: '6px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', fontWeight: 500,
        transform: hovered ? 'translateY(-1px)' : 'translateY(0)',
        boxShadow: hovered ? '0 3px 8px rgba(0,0,0,0.12)' : 'none',
        transition: 'all 0.15s ease', display: 'inline-flex', alignItems: 'center', gap: '4px', ...style,
      }}>
      {children}
    </button>
  );
};

// ── Bloc commission visible par la commerciale ────────────────────────────────
const CommissionBlocCommerciale = ({ profile, mois }) => {
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    if (!profile?.id) return;
    Promise.all([
      supabase.from('commissions').select('*').eq('user_id', profile.id).eq('mois', mois),
      supabase.from('quotes').select('id, total_with_vat, statut, created_at').eq('user_id', profile.id),
    ]).then(([{ data: comms }, { data: qs }]) => {
      const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
      const myQuotesMois = (qs || []).filter(q => q.created_at?.startsWith(mois));
      const nbDevis = myQuotesMois.length;
      const fixeRef = profile.fixe_reference || 140000;
      const fixe = nbDevis === 0 ? Math.round(fixeRef * 0.7) : nbDevis === 1 ? Math.round(fixeRef * 0.85) : fixeRef;
      const commTotal = (comms || []).reduce((s, c) => s + (c.commission_calculee || 0), 0);
      const commVersee = (comms || []).filter(c => c.statut === 'versee').reduce((s, c) => s + (c.commission_calculee || 0), 0);
      const commAttente = (comms || []).filter(c => c.statut === 'en_attente').reduce((s, c) => s + (c.commission_calculee || 0), 0);
      setData({ nbDevis, fixe, fixeRef, commTotal, commVersee, commAttente, comms: comms || [], fmt });
    });
  }, [profile, mois]);

  if (!data) return null;
  const isDetail = profile?.visibilite_commissions === 'detail';

  return (
    <div style={{ background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', border: '1px solid #fcd34d',
      borderRadius: '12px', padding: '16px 20px', marginBottom: '20px' }}>
      <div style={{ fontWeight: 700, fontSize: '14px', color: '#92400e', marginBottom: '12px' }}>
        💰 Mes commissions — {new Date(mois + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
      </div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ background: 'white', borderRadius: '8px', padding: '10px 16px', border: '1px solid #fcd34d', textAlign: 'center' }}>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e40af' }}>{data.nbDevis}</div>
          <div style={{ fontSize: '10px', color: '#6b7280' }}>devis ce mois</div>
        </div>
        <div style={{ background: 'white', borderRadius: '8px', padding: '10px 16px', border: '1px solid #fcd34d', textAlign: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#059669' }}>{data.fmt(data.fixe)} FCFA</div>
          <div style={{ fontSize: '10px', color: '#6b7280' }}>fixe du mois</div>
        </div>
        {isDetail && (
          <>
            <div style={{ background: 'white', borderRadius: '8px', padding: '10px 16px', border: '1px solid #fcd34d', textAlign: 'center' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#f59e0b' }}>{data.fmt(data.commAttente)} FCFA</div>
              <div style={{ fontSize: '10px', color: '#6b7280' }}>en attente</div>
            </div>
            <div style={{ background: 'white', borderRadius: '8px', padding: '10px 16px', border: '1px solid #fcd34d', textAlign: 'center' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#10b981' }}>{data.fmt(data.commVersee)} FCFA</div>
              <div style={{ fontSize: '10px', color: '#6b7280' }}>versée</div>
            </div>
          </>
        )}
        <div style={{ background: '#92400e', borderRadius: '8px', padding: '10px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: 'white' }}>{data.fmt(data.fixe + data.commTotal)} FCFA</div>
          <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)' }}>total estimé</div>
        </div>
      </div>
      {isDetail && data.comms.length > 0 && (
        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {data.comms.map(comm => (
            <div key={comm.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'white', borderRadius: '6px', padding: '8px 12px', border: '1px solid #fef3c7', fontSize: '12px' }}>
              <span style={{ color: '#374151' }}>Devis #{comm.quote_id} — CA : {data.fmt(comm.ca_installe_paye)} FCFA</span>
              <span style={{ fontWeight: 700, color: '#f59e0b' }}>{data.fmt(comm.commission_calculee)} FCFA</span>
              <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                background: comm.statut === 'versee' ? '#d1fae5' : comm.statut === 'a_verser' ? '#fef3c7' : '#f3f4f6',
                color: comm.statut === 'versee' ? '#065f46' : comm.statut === 'a_verser' ? '#92400e' : '#6b7280' }}>
                {comm.statut === 'versee' ? '✅ Versée' : comm.statut === 'a_verser' ? '🟡 À verser' : '⏳ En attente'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
const QuoteDashboard = () => {
  const navigate = useNavigate();
  const { profile, signOut, user } = useAuth();
  const { loadQuoteData, resetQuoteData, updateQuoteData } = useQuote();

  const [quotes, setQuotes] = useState([]);
  const [commerciales, setCommerciales] = useState([]);
  const [assignModal, setAssignModal] = useState(null); // { quoteId, quoteNum }
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('tous');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [invoiceQuote, setInvoiceQuote] = useState(null);
  const [expandedQuote, setExpandedQuote] = useState(null);
  const [quoteInvoices, setQuoteInvoices] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [toast, setToast] = useState(null);
  const [notesQuote, setNotesQuote] = useState(null);
  const [allReminders, setAllReminders] = useState([]);
  const [showAlertsPanel, setShowAlertsPanel] = useState(false);
  const [reminderCounts, setReminderCounts] = useState({});
  const [showClients, setShowClients] = useState(false);
  const [vue, setVue] = useState('devis');
  const location = useLocation();
  React.useEffect(() => { if (location.search.includes('vue=clients')) { setVue('clients'); setShowClients(true); } }, [location.search]);

  // ── Performance ────────────────────────────────────────────────────────────
  const [teamStats, setTeamStats] = useState([]);
  const [perfLoaded, setPerfLoaded] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchCommerciales = useCallback(async () => {
    const { data } = await supabase.from('profiles').select('id, nom').eq('role', 'commerciale').order('nom');
    if (data) setCommerciales(data);
  }, []);

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.from('quotes').select('*').order('created_at', { ascending: false });
    if (!error) setQuotes(data || []);
    else showToast('Erreur de chargement', 'error');
    setLoading(false);
  }, []);

  const fetchTeamStats = useCallback(async () => {
    const { data } = await supabase.rpc('get_team_conversion_stats');
    if (data) setTeamStats(data);
    setPerfLoaded(true);
  }, []);

  const fetchReminders = useCallback(async () => {
    const { data } = await supabase
      .from('quote_reminders')
      .select('*, quotes(quote_number, client_info)')
      .eq('statut', 'pending')
      .lte('remind_at', todayStr())
      .order('remind_at', { ascending: true });
    setAllReminders(data || []);

    const { data: allPending } = await supabase
      .from('quote_reminders').select('quote_id, remind_at').eq('statut', 'pending');
    const counts = {};
    const today = todayStr();
    (allPending || []).forEach(r => {
      if (!counts[r.quote_id]) counts[r.quote_id] = { total: 0, urgent: 0 };
      counts[r.quote_id].total++;
      if (r.remind_at <= today) counts[r.quote_id].urgent++;
    });
    setReminderCounts(counts);
  }, []);

useEffect(() => {
  fetchQuotes();
  if (profile?.role === 'manager') fetchCommerciales();
  fetchTeamStats();
  const interval = setInterval(() => { fetchQuotes(); fetchTeamStats(); }, 60000);
  return () => clearInterval(interval);
}, [fetchQuotes, fetchTeamStats, fetchCommerciales, profile]);
  useEffect(() => { fetchReminders(); }, [fetchReminders]);

  const fetchInvoicesForQuote = async (quoteId) => {
    if (quoteInvoices[quoteId]) { setExpandedQuote(expandedQuote === quoteId ? null : quoteId); return; }
    const { data } = await supabase.from('invoices').select('*').eq('quote_id', quoteId).order('echeance_numero');
    setQuoteInvoices(prev => ({ ...prev, [quoteId]: data || [] }));
    setExpandedQuote(quoteId);
  };

  const handleDownloadInvoicePDF = async (invoice, quote) => {
    const LOGO_URL = 'https://devis.takoussane.com/logo-takoussane.png';
    buildInvoicePDF(invoice, quote, LOGO_URL).save(`${invoice.invoice_number}.pdf`);
  };

  const filtered = quotes.filter(q => {
    if (!showArchived && q.statut === 'archive') return false;
    if (showArchived && q.statut !== 'archive') return false;
    const clientName = q.client_info?.clientName || q.client_info?.name || '';
    const matchSearch = !search || clientName.toLowerCase().includes(search.toLowerCase()) || q.quote_number?.toLowerCase().includes(search.toLowerCase());
    const matchStatut = filterStatut === 'tous' || q.statut === filterStatut;
    const matchFrom = !filterDateFrom || q.date >= filterDateFrom;
    const matchTo = !filterDateTo || q.date <= filterDateTo;
    return matchSearch && matchStatut && matchFrom && matchTo;
  });

  const handleStatutChange = async (id, newStatut) => {
    setUpdating(id);
    const { error } = await supabase.from('quotes').update({ statut: newStatut }).eq('id', id);
    if (!error) { setQuotes(prev => prev.map(q => q.id === id ? { ...q, statut: newStatut } : q)); showToast('Statut mis à jour'); }
    else showToast('Erreur', 'error');
    setUpdating(null);
  };

  const handleDelete = async (id) => {
    const { error } = await supabase.from('quotes').update({ statut: 'archive' }).eq('id', id);
    if (!error) { setQuotes(prev => prev.map(q => q.id === id ? { ...q, statut: 'archive' } : q)); showToast('Devis archivé'); }
    else showToast('Erreur', 'error');
    setDeleteConfirm(null);
  };

  const handleAssign = async (quoteId, userId) => {
    await supabase.from('quotes').update({ user_id: userId }).eq('id', quoteId);
    setAssignModal(null);
    fetchQuotes();
  };

  const handleRestore = async (id) => {
    const { error } = await supabase.from('quotes').update({ statut: 'en_cours' }).eq('id', id);
    if (!error) { setQuotes(prev => prev.map(q => q.id === id ? { ...q, statut: 'en_cours' } : q)); showToast('Devis restauré'); }
    else showToast('Erreur', 'error');
  };

  const handleDeletePermanent = async (id) => {
    const { error } = await supabase.from('quotes').delete().eq('id', id);
    if (!error) { setQuotes(prev => prev.filter(q => q.id !== id)); showToast('Supprimé définitivement'); }
    else showToast('Erreur', 'error');
    setDeleteConfirm(null);
  };

  const loadQuote = (quote) => {
    const payload = {
      clientInfo: quote.client_info, companyInfo: quote.company_info, items: quote.items,
      deliveryCost: quote.delivery_cost, vatOption: quote.vat_option, discount: quote.discount, discountPct: quote.discount_pct || 0,
      discountPct: quote.discount_pct || 0,
      conditionsValidite: quote.conditions_validite || '',
      conditionsPaiement: quote.conditions_paiement || '',
      conditionsGaranties: quote.conditions_garanties || '',
      subtotal: quote.subtotal, vat: quote.vat, totalWithVat: quote.total_with_vat,
      quoteNumber: quote.quote_number, date: quote.date,
    };
    if (loadQuoteData) loadQuoteData(payload); else if (updateQuoteData) updateQuoteData(payload);
  };

  const handleConsult = (quote) => { loadQuote(quote); navigate('/confirmation'); };
  const handleEdit = (quote) => { loadQuote(quote); navigate('/client-info'); };
  const handleNewQuote = () => { if (resetQuoteData) resetQuoteData(); navigate('/client-info'); };
  const handleSignOut = async () => { await signOut(); navigate('/login'); };

  const handleAlertDone = async (reminderId) => {
    await supabase.from('quote_reminders').update({ statut: 'done' }).eq('id', reminderId);
    setAllReminders(prev => prev.filter(r => r.id !== reminderId));
    await fetchReminders();
  };

  // ── Calculs stats ──────────────────────────────────────────────────────────
  const totalDevis = quotes.filter(q => q.statut !== 'archive').length;
  const totalGagne = quotes.filter(q => q.statut === 'gagne').length;
  const totalCA = quotes.filter(q => q.statut === 'gagne').reduce((s, q) => s + (q.total_with_vat || 0), 0);
  const totalEnCours = quotes.filter(q => q.statut === 'en_cours').length;
  const urgentCount = allReminders.length;

  // ── Calculs performance ────────────────────────────────────────────────────
  const tauxConversion = totalDevis > 0 ? Math.round((totalGagne / totalDevis) * 100) : 0;
  const sortedTeam = [...teamStats].sort((a, b) => b.taux_conversion - a.taux_conversion);
  const monRang = sortedTeam.findIndex(s => s.commerciale_id === user?.id) + 1;
  const totalCommerciales = teamStats.length;
  const perfLabel = tauxConversion >= 40 ? '🟢 Performant(e)' : tauxConversion >= 20 ? '🟡 En progression' : '🔴 À améliorer';
  const perfColor = tauxConversion >= 40 ? '#10b981' : tauxConversion >= 20 ? '#f59e0b' : '#ef4444';
  const perfBg = tauxConversion >= 40 ? '#d1fae5' : tauxConversion >= 20 ? '#fef3c7' : '#fee2e2';

  return (
    <>
      {assignModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '28px', maxWidth: '380px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>Assigner le devis</div>
            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '20px' }}>Devis <strong>{assignModal.quoteNum}</strong> — choisir la commerciale</div>
            <select onChange={e => e.target.value && handleAssign(assignModal.quoteId, e.target.value)}
              defaultValue=""
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', outline: 'none', marginBottom: '16px', cursor: 'pointer' }}>
              <option value="" disabled>Sélectionner une commerciale...</option>
              {commerciales.map(cm => <option key={cm.id} value={cm.id}>{cm.nom}</option>)}
            </select>
            <button onClick={() => setAssignModal(null)}
              style={{ width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px', background: 'white', fontSize: '14px', fontWeight: 600, cursor: 'pointer', color: '#374151' }}>
              Annuler
            </button>
          </div>
        </div>
      )}
    <div style={{ minHeight: '100vh', background: '#f0f4f8', fontFamily: 'Segoe UI, sans-serif', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      {/* ── HEADER ── */}
      <div style={{ background: 'linear-gradient(135deg, #1e40af, #3b82f6)', padding: '16px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {profile?.role === 'manager' && (
            <button onClick={() => navigate('/manager-dashboard')}
              onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.28)'}
              onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.15)'}
              style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
                borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', transition: 'all 0.15s' }}>
              ⚙ Console Manager
            </button>
          )}
          <h1 style={{ color: 'white', fontSize: '22px', fontWeight: 700, margin: 0 }}>📋 Mon Espace</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>

          {/* Centre d'aide */}
          <HelpCenter />

          {/* Recherche globale */}
          <GlobalSearch compact={profile?.role === 'manager'} />

          {/* Alertes */}
          <button onClick={() => setShowAlertsPanel(!showAlertsPanel)}
            onMouseEnter={e => { e.currentTarget.style.background=urgentCount>0?'rgba(239,68,68,0.5)':'rgba(255,255,255,0.28)'; }}
            onMouseLeave={e => { e.currentTarget.style.background=urgentCount>0?'rgba(239,68,68,0.3)':'rgba(255,255,255,0.15)'; }}
            style={{ background: urgentCount>0?'rgba(239,68,68,0.3)':'rgba(255,255,255,0.15)',
              border: `1px solid ${urgentCount>0?'rgba(239,68,68,0.6)':'rgba(255,255,255,0.3)'}`,
              color: 'white', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer',
              fontSize: '13px', transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            🔔 Alertes
            {urgentCount > 0 && (
              <span style={{ background: '#ef4444', color: 'white', borderRadius: '999px', padding: '1px 7px', fontSize: '11px', fontWeight: 700 }}>
                {urgentCount}
              </span>
            )}
          </button>

          {/* Nom */}
          <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <User size={13} /><span>{profile?.nom}</span>
            {profile?.role === 'manager' && (
              <span style={{ marginLeft: '6px', fontSize: '11px', background: 'rgba(255,255,255,0.2)', padding: '2px 8px', borderRadius: '999px' }}>Manager</span>
            )}
          </span>

          {/* Mes devis */}
          <button onClick={() => { setShowClients(false); setVue('devis'); }}
            style={{ background: vue==='devis' ? 'white' : 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: vue==='devis' ? '#1e40af' : 'white',
              borderRadius: '8px', padding: '8px 14px', cursor: 'pointer',
              fontSize: '13px', fontWeight: 600, transition: 'all 0.15s',
              display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            📋 Mes devis
          </button>

          {/* Mes Factures */}
          <button onClick={() => navigate('/invoice-dashboard')}
            onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.28)'; }}
            onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.15)'; }}
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
              color: 'white', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer',
              fontSize: '13px', transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            🧾 Mes Factures
          </button>

          {/* Mes clients */}
          <button onClick={() => { setShowClients(true); setVue('clients'); }}
            style={{ background: vue==='clients' ? 'white' : 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: vue==='clients' ? '#1e40af' : 'white',
              borderRadius: '8px', padding: '8px 14px', cursor: 'pointer',
              fontSize: '13px', fontWeight: 600, transition: 'all 0.15s',
              display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            👥 Mes clients
          </button>

          {/* Nouveau devis */}
          <button onClick={handleNewQuote}
            onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.28)'; }}
            onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.15)'; }}
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
              color: 'white', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer',
              fontSize: '14px', fontWeight: 600, transition: 'all 0.15s' }}>
            + Nouveau devis
          </button>

          {/* Déconnexion */}
          <button onClick={handleSignOut}
            onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.28)'; }}
            onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.15)'; }}
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
              color: 'white', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer',
              fontSize: '13px', transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            Déconnexion
          </button>
        </div>
      </div>
      {/* Bandeau alertes */}
      <AnimatePresence>
        {showAlertsPanel && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            style={{ overflow: 'hidden', background: urgentCount>0?'#fef2f2':'#f0fdf4',
              borderBottom: `2px solid ${urgentCount>0?'#fca5a5':'#86efac'}` }}>
            <div style={{ padding: '16px 32px', maxWidth: '1400px', margin: '0 auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontWeight: 700, fontSize: '14px', color: urgentCount>0?'#b91c1c':'#065f46' }}>
                  {urgentCount>0 ? `🔴 ${urgentCount} rappel(s) à traiter` : '✅ Aucun rappel urgent'}
                </span>
                <button onClick={() => setShowAlertsPanel(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '20px' }}>×</button>
              </div>
              {allReminders.length === 0 ? (
                <p style={{ fontSize: '13px', color: '#6b7280' }}>Tout est à jour — aucun rappel en retard.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {allReminders.map(r => {
                    const clientName = r.quotes?.client_info?.clientName || r.quotes?.client_info?.name || '—';
                    const isOverdue = r.remind_at < todayStr();
                    return (
                      <div key={r.id} style={{ background: 'white', borderRadius: '8px', padding: '10px 14px',
                        border: `1px solid ${isOverdue?'#fca5a5':'#fcd34d'}`,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <span style={{ fontWeight: 700, fontSize: '13px', color: isOverdue?'#ef4444':'#f59e0b', marginRight: '8px' }}>
                            {isOverdue ? "⚠️ En retard" : "🔔 Aujourd'hui"}
                          </span>
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>
                            {r.quotes?.quote_number} — {clientName}
                          </span>
                          <div style={{ fontSize: '13px', color: '#1f2937', marginTop: '2px' }}>{r.message}</div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginLeft: '12px' }}>
                          <button onClick={() => { const q = quotes.find(q => q.id === r.quote_id); if (q) setNotesQuote(q); setShowAlertsPanel(false); }}
                            onMouseEnter={e => { e.currentTarget.style.background='#dbeafe'; e.currentTarget.style.borderColor='#93c5fd'; }}
                            onMouseLeave={e => { e.currentTarget.style.background='#eff6ff'; e.currentTarget.style.borderColor='#bfdbfe'; }}
                            style={{ background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe',
                              borderRadius: '6px', padding: '5px 10px', fontSize: '11px', cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s' }}>
                            Ouvrir
                          </button>
                          <button onClick={() => handleAlertDone(r.id)}
                            onMouseEnter={e => { e.currentTarget.style.background='#dcfce7'; e.currentTarget.style.borderColor='#86efac'; }}
                            onMouseLeave={e => { e.currentTarget.style.background='#f0fdf4'; e.currentTarget.style.borderColor='#bbf7d0'; }}
                            style={{ background: '#f0fdf4', color: '#10b981', border: '1px solid #bbf7d0',
                              borderRadius: '6px', padding: '5px 10px', fontSize: '11px', cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s' }}>
                            ✓ Fait
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{ padding: '24px 32px', maxWidth: '1400px', margin: '0 auto', width: '100%' }}>

        {/* Bandeau performance — masqué pour le manager */}
        {perfLoaded && profile?.role !== 'manager' && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            style={{ background: perfBg, border: `1px solid ${perfColor}`,
              borderRadius: '12px', padding: '14px 20px', marginBottom: '20px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>Ma performance</div>
                <div style={{ fontWeight: 700, fontSize: '14px', color: perfColor }}>{perfLabel}</div>
              </div>
              <div style={{ width: '1px', height: '36px', background: perfColor, opacity: 0.3 }} />
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>Taux de conversion</div>
                <div style={{ fontWeight: 700, fontSize: '22px', color: perfColor }}>{tauxConversion}%</div>
              </div>
              <div style={{ width: '1px', height: '36px', background: perfColor, opacity: 0.3 }} />
              <div>
<div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>Rang dans Équipe 07</div>
<div style={{ fontWeight: 700, fontSize: '22px', color: perfColor }}>
  #{monRang > 0 ? monRang : '—'} <span style={{ fontSize: '12px', fontWeight: 400 }}>sur {teamStats.length} commerciales</span>
</div>
              </div>
            </div>
            {/* Barre progression */}
            <div style={{ minWidth: '200px', flex: 1, maxWidth: '280px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#6b7280', marginBottom: '4px' }}>
                <span>0%</span><span>Objectif 40%</span><span>100%</span>
              </div>
              <div style={{ background: 'rgba(0,0,0,0.1)', borderRadius: '999px', height: '8px', position: 'relative' }}>
                <div style={{ width: `${Math.min(tauxConversion, 100)}%`, height: '100%',
                  background: perfColor, borderRadius: '999px', transition: 'width 1s ease' }} />
                <div style={{ position: 'absolute', left: '40%', top: '-2px', width: '2px',
                  height: '12px', background: '#6b7280', opacity: 0.5 }} />
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Bloc commissions — visible selon visibilite_commissions ── */}
        {profile?.role !== 'manager' && profile?.visibilite_commissions !== 'masque' && profile?.visibilite_commissions && (
          <CommissionBlocCommerciale profile={profile} mois={new Date().toISOString().slice(0,7)} />
        )}
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          {[
            { label: 'Total devis', value: totalDevis, icon: '📄', color: '#3b82f6' },
            { label: 'En cours', value: totalEnCours, icon: '⏳', color: '#f59e0b' },
            { label: 'Gagnés', value: totalGagne, icon: '✅', color: '#10b981' },
            { label: 'CA Gagné (FCFA)', value: fmt(totalCA), icon: '💰', color: '#8b5cf6' },
          ].map((stat, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
              style={{ background: 'white', borderRadius: '12px', padding: '20px',
                boxShadow: '0 1px 6px rgba(0,0,0,0.08)', borderLeft: `4px solid ${stat.color}` }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>{stat.icon}</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: stat.color }}>{stat.value}</div>
              <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Filtres */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px 20px',
          boxShadow: '0 1px 6px rgba(0,0,0,0.08)', marginBottom: '20px',
          display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="🔍 Rechercher client, N° devis..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: '1', minWidth: '200px', border: '1px solid #e5e7eb', borderRadius: '8px',
              padding: '8px 12px', fontSize: '14px', outline: 'none' }} />
          <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}
            style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 12px', fontSize: '14px', outline: 'none', background: 'white' }}>
            <option value="tous">Tous les statuts</option>
            <option value="en_cours">En cours</option>
            <option value="gagne">Gagné</option>
            <option value="perdu">Perdu</option>
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Du</span>
            <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
              style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 10px', fontSize: '14px', outline: 'none' }} />
            <span style={{ fontSize: '13px', color: '#6b7280' }}>au</span>
            <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
              style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 10px', fontSize: '14px', outline: 'none' }} />
          </div>
          {(search || filterStatut !== 'tous' || filterDateFrom || filterDateTo) && (
            <button onClick={() => { setSearch(''); setFilterStatut('tous'); setFilterDateFrom(''); setFilterDateTo(''); }}
              onMouseEnter={e => { e.currentTarget.style.background='#f3f4f6'; e.currentTarget.style.borderColor='#9ca3af'; e.currentTarget.style.color='#374151'; }}
              onMouseLeave={e => { e.currentTarget.style.background='#f9fafb'; e.currentTarget.style.borderColor='#e5e7eb'; e.currentTarget.style.color='#6b7280'; }}
              style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 12px',
                fontSize: '13px', cursor: 'pointer', background: '#f9fafb', color: '#6b7280', transition: 'all 0.15s' }}>
              ✕ Réinitialiser
            </button>
          )}
          {profile?.role === 'manager' && (
            <button onClick={() => { setShowArchived(!showArchived); setFilterStatut('tous'); }}
              onMouseEnter={e => { if (!showArchived) { e.currentTarget.style.background='#e5e7eb'; e.currentTarget.style.color='#374151'; } else { e.currentTarget.style.background='#4b5563'; } }}
              onMouseLeave={e => { e.currentTarget.style.background=showArchived?'#6b7280':'#f9fafb'; e.currentTarget.style.color=showArchived?'white':'#6b7280'; }}
              style={{ border: `1px solid ${showArchived?'#6b7280':'#e5e7eb'}`, borderRadius: '8px', padding: '8px 12px',
                fontSize: '13px', cursor: 'pointer', background: showArchived?'#6b7280':'#f9fafb',
                color: showArchived?'white':'#6b7280', fontWeight: showArchived?600:400, transition: 'all 0.15s' }}>
              🗄️ {showArchived ? 'Masquer archives' : 'Voir archives'}
            </button>
          )}
        </div>

        {/* Panneau Clients */}
        {showClients && (
          <div style={{ background: 'white', borderRadius: '14px', padding: '20px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
            <div style={{ fontWeight: 700, fontSize: '16px', marginBottom: '16px', color: '#111827' }}>
              👥 Mes clients
            </div>
            <ClientsPanel isManager={profile?.role === 'manager'} />
          </div>
        )}

        {/* Tableau */}
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 6px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>Chargement des devis...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
              <div>Aucun devis actif</div>
              {quotes.filter(q => q.statut === 'archive').length > 0 && (
                <div style={{ fontSize: '13px', marginTop: '8px', color: '#6b7280' }}>
                  📁 {quotes.filter(q => q.statut === 'archive').length} devis archivé(s) — contactez votre manager
                </div>
              )}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
                  {[
                    { label: 'N° Devis',    width: '12%' },
                    { label: 'Date',        width: '9%'  },
                    { label: 'Client',      width: '19%' },
                    { label: 'Montant TTC', width: '13%' },
                    { label: 'Statut',      width: '12%' },
                    { label: 'Actions',     width: '35%' },
                  ].map(({ label, width }) => (
                    <th key={label} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px',
                      fontWeight: 600, color: '#374151', width }}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {filtered.map((quote, idx) => {
                    const clientName = quote.client_info?.clientName || quote.client_info?.name || '—';
                    const statut = STATUTS[quote.statut] || STATUTS.en_cours;
                    const rc = reminderCounts[quote.id];
                    const hasUrgent = rc?.urgent > 0;
                    const hasPending = rc?.total > 0;
                    return (
                      <React.Fragment key={quote.id}>
                        <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                          transition={{ delay: idx * 0.03 }}
                          style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                          onMouseLeave={e => e.currentTarget.style.background='white'}>
                          <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: 600, color: '#1e40af' }}>{quote.quote_number}</td>
                          <td style={{ padding: '14px 16px', fontSize: '14px', color: '#374151' }}>{fmtDate(quote.date)}</td>
                          <td style={{ padding: '14px 16px', fontSize: '14px', color: '#374151' }}>{clientName}</td>
                          <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: 600, color: '#111827' }}>{fmt(quote.total_with_vat)} FCFA</td>
                          <td style={{ padding: '14px 16px' }}>
                            <select value={quote.statut} onChange={e => handleStatutChange(quote.id, e.target.value)}
                              disabled={updating === quote.id}
                              style={{ background: statut.bg, color: statut.color, border: `1px solid ${statut.color}`,
                                borderRadius: '999px', padding: '4px 10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', outline: 'none' }}>
                              <option value="en_cours">En cours</option>
                              <option value="gagne">Gagné</option>
                              <option value="perdu">Perdu</option>
                            </select>
                          </td>
                          <td style={{ padding: '14px 16px' }}>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              <Btn onClick={() => fetchInvoicesForQuote(quote.id)}
                                bg={expandedQuote===quote.id?'#e0e7ff':'#f5f3ff'} color="#7c3aed" border="#ddd6fe"
                                hoverBg="#ede9fe" hoverBorder="#a78bfa" hoverColor="#5b21b6">
                                {expandedQuote===quote.id?'▲ Factures':'▼ Factures'}
                              </Btn>
                              {quote.statut === 'gagne' && (
                                <Btn onClick={() => setInvoiceQuote(quote)}
                                  bg="#f0fdf4" color="#10b981" border="#bbf7d0"
                                  hoverBg="#dcfce7" hoverBorder="#6ee7b7" hoverColor="#065f46">
                                  📄 Facturer
                                </Btn>
                              )}
                              {quote.statut !== 'archive' && (
                                <Btn onClick={() => handleConsult(quote)}
                                  bg="#eff6ff" color="#1e40af" border="#bfdbfe"
                                  hoverBg="#dbeafe" hoverBorder="#93c5fd" hoverColor="#1e3a8a">
                                  📋 Consulter
                                </Btn>
                              )}
                              {quote.statut !== 'archive' && (
                                <Btn onClick={() => handleEdit(quote)}
                                  bg="#fefce8" color="#854d0e" border="#fde68a"
                                  hoverBg="#fef9c3" hoverBorder="#fcd34d" hoverColor="#713f12">
                                  ✏️ Modifier
                                </Btn>
                              )}
                              <button onClick={() => setNotesQuote(quote)}
                                onMouseEnter={e => { e.currentTarget.style.background=hasUrgent?'#fee2e2':hasPending?'#fef9c3':'#f3f4f6'; e.currentTarget.style.borderColor=hasUrgent?'#fca5a5':hasPending?'#fcd34d':'#d1d5db'; e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow='0 3px 8px rgba(0,0,0,0.12)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background=hasUrgent?'#fef2f2':hasPending?'#fefce8':'white'; e.currentTarget.style.borderColor=hasUrgent?'#fecaca':hasPending?'#fde68a':'#e5e7eb'; e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='none'; }}
                                style={{ background: hasUrgent?'#fef2f2':hasPending?'#fefce8':'white',
                                  color: hasUrgent?'#ef4444':hasPending?'#854d0e':'#6b7280',
                                  border: `1px solid ${hasUrgent?'#fecaca':hasPending?'#fde68a':'#e5e7eb'}`,
                                  borderRadius: '6px', padding: '6px 12px', fontSize: '12px',
                                  cursor: 'pointer', fontWeight: 500, transition: 'all 0.15s',
                                  display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                📝 Suivi
                                {hasPending && (
                                  <span style={{ background: hasUrgent?'#ef4444':'#f59e0b', color: 'white',
                                    borderRadius: '999px', padding: '1px 6px', fontSize: '10px', fontWeight: 700 }}>
                                    {rc.total}
                                  </span>
                                )}
                              </button>
                              {profile?.role==='manager' && (
                                <Btn onClick={() => setAssignModal({ quoteId: quote.id, quoteNum: quote.quote_number })}
                                  bg='#f5f3ff' color='#7c3aed' border='#c4b5fd'
                                  hoverBg='#ede9fe' hoverBorder='#a78bfa' hoverColor='#5b21b6'>
                                  👤 Assigner
                                </Btn>
                              )}
                              {profile?.role==='manager' && quote.statut!=='archive' && (
                                <Btn onClick={() => setDeleteConfirm(quote.id)}
                                  bg="#fef2f2" color="#ef4444" border="#fecaca"
                                  hoverBg="#fee2e2" hoverBorder="#fca5a5" hoverColor="#b91c1c">
                                  Archiver
                                </Btn>
                              )}
                              {profile?.role==='manager' && quote.statut==='archive' && (
                                <Btn onClick={() => handleRestore(quote.id)}
                                  bg="#f0fdf4" color="#10b981" border="#bbf7d0"
                                  hoverBg="#dcfce7" hoverBorder="#6ee7b7" hoverColor="#065f46">
                                  Restaurer
                                </Btn>
                              )}
                              {profile?.role==='manager' && quote.statut==='archive' && (
                                <Btn onClick={() => setDeleteConfirm(quote.id)}
                                  bg="#fef2f2" color="#ef4444" border="#fecaca"
                                  hoverBg="#fee2e2" hoverBorder="#fca5a5" hoverColor="#b91c1c">
                                  Supprimer
                                </Btn>
                              )}
                            </div>
                          </td>
                        </motion.tr>

                        {/* Accordéon factures */}
                        {expandedQuote === quote.id && (
                          <tr>
                            <td colSpan="6" style={{ padding: '0', background: '#f8fafc' }}>
                              <div style={{ padding: '16px 24px', borderBottom: '2px solid #e5e7eb' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                  <span style={{ fontWeight: 600, fontSize: '14px', color: '#1e40af' }}>🧾 Factures liées à ce devis</span>
                                  <button onClick={() => navigate('/invoice-dashboard')}
                                    onMouseEnter={e => e.currentTarget.style.color='#1e40af'}
                                    onMouseLeave={e => e.currentTarget.style.color='#6b7280'}
                                    style={{ fontSize: '12px', color: '#6b7280', background: 'none', border: 'none',
                                      cursor: 'pointer', textDecoration: 'underline', transition: 'color 0.15s' }}>
                                    Voir toutes les factures →
                                  </button>
                                </div>
                                {(quoteInvoices[quote.id]||[]).length===0 ? (
                                  <p style={{ fontSize: '13px', color: '#9ca3af' }}>Aucune facture générée</p>
                                ) : (
                                  <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                                    <thead>
                                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        {['N° Facture','Type','Montant','Statut','PDF'].map(h => (
                                          <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>{h}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(quoteInvoices[quote.id]||[]).map(inv => {
                                        const typeLabel = inv.type==='echeance'?`Échéance ${inv.echeance_numero-1}/${inv.echeance_total-1}`:inv.type==='acompte'?'Acompte':'Solde';
                                        const sColors={generee:'#6b7280',emise:'#f59e0b',en_attente:'#3b82f6',payee:'#10b981',annulee:'#ef4444'};
                                        const sLabels={generee:'Générée',emise:'Émise',en_attente:'En attente',payee:'Payée',annulee:'Annulée'};
                                        return (
                                          <tr key={inv.id} style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.12s' }}
                                            onMouseEnter={e => e.currentTarget.style.background='#f0f9ff'}
                                            onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                                            <td style={{ padding: '8px 10px', fontWeight: 600, color: '#1e40af' }}>{inv.invoice_number}</td>
                                            <td style={{ padding: '8px 10px' }}>{typeLabel}</td>
                                            <td style={{ padding: '8px 10px', fontWeight: 600 }}>{new Intl.NumberFormat('fr-FR').format(Math.round(inv.montant))} FCFA</td>
                                            <td style={{ padding: '8px 10px' }}>
                                              <span
                                                title={
                                                  inv.statut === 'generee' ? 'Facture créée, pas encore envoyée au client' :
                                                  inv.statut === 'emise' ? 'Facture envoyée au client' :
                                                  inv.statut === 'en_attente' ? 'Échéance future, pas encore due' :
                                                  inv.statut === 'payee' ? 'Paiement reçu ✓' :
                                                  inv.statut === 'annulee' ? 'Facture annulée' : ''
                                                }
                                                style={{ background:(sColors[inv.statut]||'#9ca3af')+'20', color:sColors[inv.statut]||'#9ca3af',
                                                  border:`1px solid ${sColors[inv.statut]||'#9ca3af'}`, borderRadius:'999px', padding:'2px 8px',
                                                  fontSize:'11px', fontWeight:600, cursor:'help' }}>
                                                {sLabels[inv.statut]||inv.statut}
                                              </span>
                                            </td>
                                            <td style={{ padding: '8px 10px' }}>
                                              <button onClick={() => handleDownloadInvoicePDF(inv, quote)}
                                                onMouseEnter={e => { e.currentTarget.style.background='#dbeafe'; e.currentTarget.style.borderColor='#93c5fd'; e.currentTarget.style.color='#1e3a8a'; e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow='0 2px 6px rgba(30,64,175,0.15)'; }}
                                                onMouseLeave={e => { e.currentTarget.style.background='#eff6ff'; e.currentTarget.style.borderColor='#bfdbfe'; e.currentTarget.style.color='#1e40af'; e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='none'; }}
                                                style={{ background:'#eff6ff', color:'#1e40af', border:'1px solid #bfdbfe', borderRadius:'6px', padding:'4px 10px', fontSize:'11px', cursor:'pointer', transition:'all 0.15s' }}>
                                                📥 PDF
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          )}
        </div>
        <div style={{ marginTop: '12px', fontSize: '13px', color: '#9ca3af', textAlign: 'right' }}>
          {filtered.length} devis affichés sur {quotes.length} au total
        </div>
      </div>

      {/* Modal suppression */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
              style={{ background: 'white', borderRadius: '16px', padding: '32px', maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>{showArchived?'🗑️':'🗄️'}</div>
              <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '8px' }}>{showArchived?'Supprimer définitivement ?':'Archiver ce devis ?'}</h3>
              <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '24px' }}>{showArchived?'Cette action est irréversible.':'Le devis sera archivé et récupérable.'}</p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button onClick={() => setDeleteConfirm(null)}
                  onMouseEnter={e => { e.currentTarget.style.background='#f3f4f6'; e.currentTarget.style.borderColor='#9ca3af'; }}
                  onMouseLeave={e => { e.currentTarget.style.background='white'; e.currentTarget.style.borderColor='#e5e7eb'; }}
                  style={{ padding: '10px 24px', borderRadius: '8px', border: '1px solid #e5e7eb', cursor: 'pointer', fontWeight: 500, background: 'white', transition: 'all 0.15s' }}>
                  Annuler
                </button>
                <button onClick={() => showArchived?handleDeletePermanent(deleteConfirm):handleDelete(deleteConfirm)}
                  onMouseEnter={e => { e.currentTarget.style.background='#dc2626'; e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow='0 4px 12px rgba(239,68,68,0.35)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background='#ef4444'; e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='none'; }}
                  style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: '#ef4444', color: 'white', cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s' }}>
                  {showArchived?'Supprimer définitivement':'Archiver'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Facture */}
      <AnimatePresence>
        {invoiceQuote && (
          <InvoiceModal quote={invoiceQuote} onClose={() => setInvoiceQuote(null)}
            onSuccess={(nb) => { const qid = invoiceQuote?.id; setInvoiceQuote(null); showToast(nb + ' facture(s) générée(s) avec succès'); if (qid) { supabase.from('invoices').select('*').eq('quote_id', qid).order('echeance_numero').then(({ data }) => { setQuoteInvoices(prev => ({ ...prev, [qid]: data || [] })); setExpandedQuote(qid); }); } }} />
        )}
      </AnimatePresence>

      {/* Panneau Notes/Rappels */}
      <AnimatePresence>
        {notesQuote && (
          <QuoteNotes quote={notesQuote} onClose={() => setNotesQuote(null)} onReminderChange={fetchReminders} />
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }}
            style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 100,
              background: toast.type==='error'?'#ef4444':'#10b981', color: 'white',
              borderRadius: '10px', padding: '12px 20px', fontSize: '14px', fontWeight: 500,
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
            {toast.type==='error'?'⚠️':'✓'} {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer style={{ background: '#1e3a7a', color: 'rgba(255,255,255,0.60)', textAlign: 'center',
        padding: '10px 24px', fontSize: '11px', letterSpacing: '0.02em',
        borderTop: '2px solid rgba(0,0,0,0.15)', marginTop: 'auto' }}>
        SolarQuotePro<sup style={{ fontSize: '8px', opacity: 0.5, verticalAlign: 'super' }}>TM</sup>
        {' '}by Takoussane Energy — v1.0.1 — © {new Date().getFullYear()}
      </footer>
      <ScrollToTop />
    </div>
    </>
  );
};

export default QuoteDashboard;
